import {
  LAYER_LABELS,
  LAYER_ORDER,
  SLOT_COUNT,
  layerInward,
  layerOutward,
  layersAdjacent,
  type LayerId,
  type Side,
} from "./types";
import type { Presence } from "../cards/types";

export interface LayerState {
  id: LayerId;
  label: string;
  /** -100 Truth … +100 Lie */
  tilt: number;
  slots: (Presence | null)[];
  /** Brief UI flash when overflow hits */
  flash: number;
}

export type BoardState = Record<LayerId, LayerState>;

export function createEmptyBoard(): BoardState {
  const board = {} as BoardState;
  for (const id of LAYER_ORDER) {
    board[id] = {
      id,
      label: LAYER_LABELS[id],
      tilt: id === "will" ? -10 : 0,
      slots: Array.from({ length: SLOT_COUNT }, () => null),
      flash: 0,
    };
  }
  return board;
}

export function forEachPresence(
  board: BoardState,
  fn: (p: Presence, layer: LayerId, slot: number) => void,
): void {
  for (const id of LAYER_ORDER) {
    board[id].slots.forEach((p, slot) => {
      if (p) fn(p, id, slot);
    });
  }
}

export function countOnBoard(board: BoardState, defId: string): number {
  let n = 0;
  forEachPresence(board, (p) => {
    if (p.defId === defId) n++;
  });
  return n;
}

/** Layer holding the most player Prayers; `fallback` wins ties (and empty boards). */
export function layerWithMostPrayers(
  board: BoardState,
  fallback: LayerId,
): LayerId {
  let best = fallback;
  let bestN = countSide(board, "player", fallback);
  for (const id of LAYER_ORDER) {
    if (id === fallback) continue;
    const n = countSide(board, "player", id);
    if (n > bestN) {
      bestN = n;
      best = id;
    }
  }
  return best;
}

export function countSide(board: BoardState, side: Side, layer?: LayerId): number {
  let n = 0;
  const layers = layer ? [layer] : LAYER_ORDER;
  for (const id of layers) {
    for (const p of board[id].slots) {
      if (p?.side === side) n++;
    }
  }
  return n;
}

export function layerHasSide(board: BoardState, layer: LayerId, side: Side): boolean {
  return board[layer].slots.some((p) => p?.side === side);
}

/** Uncontested for a side: no opposing presence in that layer. */
export function isUncontested(board: BoardState, layer: LayerId, side: Side): boolean {
  const other: Side = side === "player" ? "enemy" : "player";
  return !layerHasSide(board, layer, other);
}

export function totalPresences(board: BoardState): number {
  let n = 0;
  forEachPresence(board, () => {
    n++;
  });
  return n;
}

/** Player foothold: Will always (Secret Place anchor), or friendly presence same/adjacent. */
export function playerHasFoothold(board: BoardState, layer: LayerId): boolean {
  if (layer === "will") return true;
  if (layerHasSide(board, layer, "player")) return true;
  for (const id of LAYER_ORDER) {
    if (layersAdjacent(layer, id) && layerHasSide(board, id, "player")) return true;
  }
  return false;
}

/** Enemy foothold: Mind always, or enemy presence same/adjacent. */
export function enemyHasFoothold(board: BoardState, layer: LayerId): boolean {
  if (layer === "mind") return true;
  if (layerHasSide(board, layer, "enemy")) return true;
  for (const id of LAYER_ORDER) {
    if (layersAdjacent(layer, id) && layerHasSide(board, id, "enemy")) return true;
  }
  return false;
}

export function findEmptySlot(board: BoardState, layer: LayerId): number | null {
  const slots = board[layer].slots;
  for (let i = 0; i < slots.length; i++) {
    if (!slots[i]) return i;
  }
  return null;
}

/** Prefer empty; else first friendly replace slot. */
export function findPlaceSlot(
  board: BoardState,
  layer: LayerId,
  side: Side,
): number | null {
  const empty = findEmptySlot(board, layer);
  if (empty !== null) return empty;
  const slots = board[layer].slots;
  for (let i = 0; i < slots.length; i++) {
    if (slots[i]?.side === side) return i;
  }
  return null;
}

export function canPlaceInSlot(
  board: BoardState,
  layer: LayerId,
  slot: number,
  side: Side,
): boolean {
  if (slot < 0 || slot >= SLOT_COUNT) return false;
  const foothold =
    side === "player" ? playerHasFoothold(board, layer) : enemyHasFoothold(board, layer);
  if (!foothold) return false;
  const cur = board[layer].slots[slot];
  if (!cur) return true;
  return cur.side === side; // friendly replace
}

export function removePresence(board: BoardState, instanceId: string): Presence | null {
  for (const id of LAYER_ORDER) {
    const slots = board[id].slots;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i]?.instanceId === instanceId) {
        const p = slots[i]!;
        slots[i] = null;
        return p;
      }
    }
  }
  return null;
}

export function findPresence(
  board: BoardState,
  instanceId: string,
): { presence: Presence; layer: LayerId; slot: number } | null {
  for (const id of LAYER_ORDER) {
    const slots = board[id].slots;
    for (let i = 0; i < slots.length; i++) {
      const p = slots[i];
      if (p?.instanceId === instanceId) return { presence: p, layer: id, slot: i };
    }
  }
  return null;
}

export function allLies(board: BoardState): { presence: Presence; layer: LayerId; slot: number }[] {
  const out: { presence: Presence; layer: LayerId; slot: number }[] = [];
  forEachPresence(board, (p, layer, slot) => {
    if (p.side === "enemy") out.push({ presence: p, layer, slot });
  });
  return out;
}

export function clampTilt(t: number): number {
  return Math.max(-100, Math.min(100, t));
}

export { layerInward, layerOutward, LAYER_ORDER, SLOT_COUNT };
